if (!customElements.get('product-sticky-bar')) {
  class ProductStickyBar extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.details = this.querySelectorAll('.product-variant-accordion details');
      this.openButton = this.querySelector('.product-sticky-bar__opener__open');
      this.closeButton = this.querySelector('.product-sticky-bar__opener__close');

      this.hideVisibleButtons = this.hasAttribute('hide-visible');
      this.defaultButton = document.querySelector('.product__info-container .product-form__buttons');

      this.bindOnResie = this.onResize.bind(this);
      this.bindOnWindowScroll = debounce(this.onWindowScroll.bind(this), 0);

      this.setInfoHeight();
      setTimeout(() => {
        this.setBarHeight();
      }, 305);

      this.details.forEach((detail) => {
        const summary = detail.querySelector('summary');
        const content = detail.querySelector('.accordion__content');

        if (summary) summary.addEventListener('click', this.onDetailsSummaryClick.bind(this, detail));
        if (content) this.setVariantFieldsetMouseDrag(content);
      });

      if (this.openButton) this.openButton.addEventListener('click', this.openBar.bind(this));
      if (this.closeButton) this.closeButton.addEventListener('click', this.closeBar.bind(this));

      if (this.hideVisibleButtons && this.defaultButton) {
        this.onWindowScroll();
        window.addEventListener('scroll', this.bindOnWindowScroll);
      }

      this.resizeTimeout = null;
      window.addEventListener('resize', this.bindOnResie);

      this.setObservers(() => {
        this.setBarHeight();
      })
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.bindOnResie);
      if (this.hideVisibleButtons && this.defaultButton) {
        window.removeEventListener('scroll', this.bindOnWindowScroll);
      }
    }

    setObservers(callback) {
      const element = this;
      if (!element) return;

      let lastRect = element.getBoundingClientRect();

      // ResizeObserver – for size changes
      const resizeObserver = new ResizeObserver(() => {
        callback('size');
      });
      resizeObserver.observe(element);

      // MutationObserver – for content changes
      const mutationObserver = new MutationObserver(() => {
        callback('content');
      });
      mutationObserver.observe(element, {
        childList: true,
        subtree: true,
        characterData: true
      });

      // Position check – using requestAnimationFrame
      let running = true;
      function checkPosition() {
        if (!running) return;
        const newRect = element.getBoundingClientRect();
        if (
          newRect.top !== lastRect.top ||
          newRect.left !== lastRect.left
        ) {
          lastRect = newRect;
          callback('position');
        }
        requestAnimationFrame(checkPosition);
      }
      requestAnimationFrame(checkPosition);

      // Cleanup function
      return () => {
        running = false;
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }

    openBar() {
      this.setAttribute('open', true);
      setTimeout(() => {
        this.setBarHeight();
      }, 450);
    }

    closeBar() {
      this.removeAttribute('open');
      this.details.forEach((detail) => {
        if (detail.open == true) this.closeVariant(detail);
      });
      setTimeout(() => {
        this.setBarHeight();
      }, 305);
    }

    onDetailsSummaryClick(target) {
      this.setDropdownHeight(target);

      target.classList.add('opening');
      setTimeout(() => {
        this.details.forEach((detail) => {
          if (detail != target && detail.open == true) this.closeVariant(detail);
        });
      }, 200);
      setTimeout(() => {
        this.setBarHeight();
      }, 320);
      setTimeout(() => {
        target.classList.remove('opening');
      }, 500);
    }

    setDropdownHeight(detail) {
      const openDetail = detail ? detail : this.querySelector('.product-variant-accordion details[open]');
      this.style.setProperty('--opened-height', `${openDetail ? openDetail.dataset.contentHeight : 0}px`);
    }

    setBarHeight() {
      document.documentElement.style.setProperty(
        '--product-sticky-bar-height',
        `${Math.max(
          0,
          Math.min(this.getBoundingClientRect().bottom, window.innerHeight) -
            Math.max(this.getBoundingClientRect().top, 0)
        )}px`
      );
    }

    setInfoHeight() {
      this.style.setProperty('--info-height', `${this.querySelector('.product-sticky-bar__info').clientHeight}px`);
    }

    closeVariant(detail) {
      detail.classList.add('closing');
      detail.querySelector('summary').setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        detail.removeAttribute('open');
        detail.classList.remove('closing');
      }, 305);
    }

    setVariantFieldsetMouseDrag(fieldset) {
      if (!fieldset) return;

      let isDown = false;
      let startX = 0;
      let startY = 0;
      let scrollLeft = 0;
      let scrollTop = 0;

      const mouseMove = (event) => {
        event.preventDefault();
        if (!isDown) return;

        fieldset.classList.add('moving');

        const x = event.pageX - fieldset.offsetLeft;
        const walkX = x - startX;
        fieldset.scrollLeft = scrollLeft - walkX;

        const y = event.pageY - fieldset.offsetTop;
        const walkY = y - startY;
        fieldset.scrollTop = scrollTop - walkY;
      };

      const mouseUp = () => {
        isDown = false;
        fieldset.classList.remove('grabbing');
        fieldset.classList.remove('moving');

        fieldset.removeEventListener('mousemove', mouseMove, { passive: false });
        fieldset.removeEventListener('mouseup', mouseUp, { passive: false });
        fieldset.removeEventListener('mouseleave', mouseUp, { passive: false });
      };

      const mouseDown = (event) => {
        fieldset.classList.add('grabbing');
        isDown = true;
        startX = event.pageX - fieldset.offsetLeft;
        startY = event.pageY - fieldset.offsetTop;
        scrollLeft = fieldset.scrollLeft;
        scrollTop = fieldset.scrollTop;

        fieldset.addEventListener('mousemove', mouseMove, { passive: false });
        fieldset.addEventListener('mouseup', mouseUp, { passive: false });
        fieldset.addEventListener('mouseleave', mouseUp, { passive: false });
      };

      fieldset.addEventListener('mousedown', mouseDown, { passive: false });
    }

    onResize() {
      this.setDropdownHeight();
      setTimeout(() => {
        this.setBarHeight();
      }, 305);
      this.setInfoHeight();
    }

    onWindowScroll() {
      if (
        this.defaultButton.getBoundingClientRect().top < window.innerHeight &&
        this.defaultButton.getBoundingClientRect().bottom > 0
      ) {
        if (!this.classList.contains('hide')) {
          this.classList.add('hide');
          this.details.forEach((detail) => {
            if (detail.open == true) this.closeVariant(detail);
          });
          setTimeout(() => {
            this.setBarHeight();
          }, 305);
        }
      } else {
        if (this.classList.contains('hide')) {
          this.classList.remove('hide');
          setTimeout(() => {
            this.setBarHeight();
          }, 305);
        }

        if (this.defaultButton.getBoundingClientRect().bottom >= window.innerHeight) {
          this.classList.add('above-buy-buttons');
        } else {
          this.classList.remove('above-buy-buttons');
        }
        if (this.defaultButton.getBoundingClientRect().bottom <= 20) {
          this.classList.add('under-buy-buttons');
        } else {
          this.classList.remove('under-buy-buttons');
        }
      }
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
}
